import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  inject,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  GcTableColumn,
  GcTableComponent,
  GcTableRow,
  IpoLanguageService,
  IpoTranslateArrayPipe,
  GcIconComponent,
  GcTextComponent,
  IpoTranslatePipe,
} from '@ip-only/front-end-library';
import {
  ConstructionStatusStyle,
  GroupAgreementOrderStatusStyle,
} from '../../../../shared/status-style/status-style-maps';
import { StatusTableCellComponent } from '../../../../shared/status-table-cell/status-table-cell.component';
import { isTopLevelDomainFI } from '../../../../shared/util/deploy-helper';
import { Domain } from '../../../entities/installation-reporting-entities';
import { GroupAgreementSections } from '../../../entities/installation-reporting-mdu.entities';
import { getSectionProgressLines } from '../../../group-agreement-details-page/group-agreement-details-table/report-mdu-table-status.helper';
import { InstallationReportingMduService } from '../../../service/mdu/installation-reporting-mdu.service';
import {
  mduCellTemplateIds,
  MduCellTemplateMap,
} from '../table-columns/installation-reporting.columns';
import { configureGcTableColumns } from '../table-columns/installation-reporting.columns.utils';

@Component({
  selector: 'app-group-agreements-table',
  imports: [
    GcTableComponent,
    IpoTranslateArrayPipe,
    AsyncPipe,
    NgTemplateOutlet,
    StatusTableCellComponent,
    IpoTranslatePipe,
    GcIconComponent,
    GcTextComponent,
  ],
  templateUrl: './group-agreements-table.component.html',
  styleUrl: './group-agreements-table.component.scss',
})
export class GroupAgreementsTableComponent {
  private readonly mduService = inject(InstallationReportingMduService);
  private readonly languageService = inject(IpoLanguageService);
  private readonly router = inject(Router);

  currentAgreements = this.mduService.storedGroupAgreements;
  isDeliveryPortalFI = signal<boolean>(isTopLevelDomainFI());
  currentDomain = computed<Domain>(() =>
    this.isDeliveryPortalFI() ? 'FI' : 'SE',
  );
  tableColumns = signal<GcTableColumn[]>(
    configureGcTableColumns(this.currentDomain(), 'MDU'),
  );
  tableRows = computed<GcTableRow[]>(() => this.configureRows());

  cellTemplates = computed<MduCellTemplateMap>(() =>
    this.configureCellTemplateMap(),
  );

  sections = GroupAgreementSections;
  language$ = this.languageService.getLanguage$();

  orderStatusStyling = GroupAgreementOrderStatusStyle;
  constructionStatusStyling = ConstructionStatusStyle;

  sectionProgressLines = getSectionProgressLines;

  @ViewChild('sectionStatusTemplate', { read: TemplateRef, static: true })
  sectionStatusTemplate!: TemplateRef<any>;

  @ViewChild('latestStatusTemplate', { read: TemplateRef, static: true })
  latestStatusTemplate!: TemplateRef<any>;

  @ViewChild('deliveryPointsTemplate', { read: TemplateRef, static: true })
  deliveryPointsTemplate!: TemplateRef<any>;

  configureRows(): GcTableRow[] {
    const items = this.currentAgreements()?.items;

    if (!items) {
      return [];
    }

    return items.map((item) => {
      const sectionStatuses = item.sectionStatuses ?? {};

      return {
        address: item.name,
        deliveryPoints: item.deliveryPointDelivered ?? { total: 0, done: 0 },
        latestStatus: item,
        plotDesignDate: sectionStatuses[this.sections.PLOT_DESIGN],
        preinstallationDate: sectionStatuses[this.sections.PRE_INSTALLATION],
        plotExcavationDate: sectionStatuses[this.sections.PLOT_EXCAVATION],
        ductConnection: sectionStatuses[this.sections.DUCT_CONNECTING],
        cableBlowingDate: sectionStatuses[this.sections.CABLE_BLOWING],
        splicingAndMeasuringDate:
          sectionStatuses[this.sections.SPLICING_MEASURING],
        commissioningDate: sectionStatuses[this.sections.COMMISSION],
        id: item.id,
      };
    });
  }

  configureCellTemplateMap(): MduCellTemplateMap {
    const cellTemplateMap = {} as MduCellTemplateMap;

    for (const id of mduCellTemplateIds) {
      cellTemplateMap[id] = this.sectionStatusTemplate;
    }

    cellTemplateMap.latestStatus = this.latestStatusTemplate;
    cellTemplateMap.deliveryPoints = this.deliveryPointsTemplate;

    return cellTemplateMap;
  }

  onRowClicked(row: GcTableRow): void {
    this.navigateToMduPage(row.id);
  }

  navigateToMduPage(orderId: string): void {
    const url = `${this.router.url}/reports?typeId=MDU&id=${orderId}`;
    const [, , ifs] = this.router.url.split('?')[0].split('/');
    this.router.navigateByUrl(url, { state: { ifs } });
  }
}
